// 🔴 N°1002 #8 — Étape 1 du devis EN MODIFICATION : « Informations client ».
//
// Besoin (Mario, PROD) : en modifiant un devis existant, la première page était la fiche
// AMO en LECTURE SEULE (profil fiscal, logement, bandeau MPR). Ici : le client du dossier,
// PRÉREMPLI et MODIFIABLE, enregistré sur le CLIENT CANONIQUE (`patchClient` →
// PATCH /api/metier/clients/{id}) — ⛔ aucune copie indépendante, ⛔ aucun endpoint neuf.
//
// 🔑 RÈGLE DOCUMENTAIRE : modifier le client canonique ⛔ ne réécrit PAS le devis déjà
//    établi — ses copies (`client_nom`, `client_adresse`…) et tout PDF déjà émis restent
//    tels quels. Seuls les documents PRODUITS ENSUITE liront la fiche à jour.
// Réutilise : `patchClient` + `formulaireClient` (dossierEditHandlers), `EditText`
// (champsEdition, extraits d'InfosTab), `deriveDevisMode` (B2B ⇒ raison sociale).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { patchClient, formulaireClient } from "../dossier/dossierEditHandlers";
import { EditText } from "../dossier/champsEdition";
import { deriveDevisMode, devisModeCapabilities } from "../../../utils/devisMode";
import AdresseChantierDevis from "./AdresseChantierDevis";
import { profilChantier } from "../../../utils/tenantProfile";

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                borderRadius: 12, padding: 16 };

export default function EtapeInfosClient({ client, dossier, tenantId, onEnregistre, patch = patchClient }) {
  const [form, setForm] = useState(() => formulaireClient(client, dossier));
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Fiche rechargée (après enregistrement ou changement de dossier) ⇒ formulaire resynchronisé.
  useEffect(() => { setForm(formulaireClient(client, dossier)); }, [client, dossier]);

  if (!client) {
    return <div style={CARTE} data-testid="devis-infos-client-chargement">Chargement des informations client…</div>;
  }

  const entreprise = devisModeCapabilities(deriveDevisMode(client)).showEntreprise;
  // 🔴 P1 — bloc « Adresse du chantier » et libellé « Adresse du client » SEULEMENT pour un tenant à profil chantier (config `profil_chantier`, ⛔ pas de slug) :
  //    OMV / Climat / TEF gardent l'écran historique, strictement inchangé.
  const parcoursChantier = Boolean(profilChantier());
  const clientId = client.id || dossier?.client_id;
  const champ = (cle) => (v) => setForm((f) => ({ ...f, [cle]: v }));
  const champAdresse = (cle) => (v) => setForm((f) => ({ ...f, adresse: { ...f.adresse, [cle]: v } }));

  const enregistrer = async () => {
    setSaving(true); setErreur(null);
    const r = await patch(clientId, form, tenantId);
    setSaving(false);
    if (r.ok) { toast.success("Client mis à jour"); onEnregistre?.(); }
    else { setErreur(r.error || "Erreur sauvegarde"); toast.error(r.error || "Erreur sauvegarde"); }
  };

  return (
    <div style={{ ...CARTE, display: "grid", gap: 16 }} data-testid="devis-infos-client">
      <div>
        <div style={{ fontWeight: 500, color: "var(--oh-text)" }}>Informations client</div>
        <div className="text-xs" style={{ color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
          Les modifications sont enregistrées sur la fiche du client. Le devis déjà établi et ses
          documents émis ne sont pas modifiés.
        </div>
      </div>
      {/* 🔴 P1 — client, COORDONNÉES et adresse du CHANTIER (site du dossier) d'abord ; l'adresse du client, plus bas, a son propre libellé. */}
      {parcoursChantier && <AdresseChantierDevis client={client} dossier={dossier} onAjoutee={onEnregistre} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {entreprise ? (
          <EditText label="Raison sociale" value={form.raison_sociale} onChange={champ("raison_sociale")} testId="devis-client-raison-sociale" />
        ) : (
          <>
            <EditText label="Nom" value={form.nom} onChange={champ("nom")} testId="devis-client-nom" />
            <EditText label="Prénom" value={form.prenom} onChange={champ("prenom")} testId="devis-client-prenom" />
          </>
        )}
        <EditText label="Téléphone" type="tel" value={form.telephone} onChange={champ("telephone")} testId="devis-client-tel" />
        <EditText label="Email" type="email" value={form.email} onChange={champ("email")} testId="devis-client-email" />
        <EditText label={parcoursChantier ? "Adresse du client (siège / facturation)" : "Adresse"} value={form.adresse?.rue} onChange={champAdresse("rue")} testId="devis-client-rue" />
        <EditText label="Code postal" value={form.adresse?.code_postal} onChange={champAdresse("code_postal")} testId="devis-client-cp" />
        <EditText label="Ville" value={form.adresse?.ville} onChange={champAdresse("ville")} testId="devis-client-ville" />
      </div>
      {erreur && <div role="alert" className="text-sm" style={{ color: "var(--oh-error-text)" }}>{erreur}</div>}
      <div>
        <button type="button" onClick={enregistrer} disabled={saving || !clientId} data-testid="devis-client-enregistrer"
                style={{ height: 36, padding: "0 16px", borderRadius: 8, background: "transparent",
                         color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" }}>
          {saving ? "Enregistrement…" : "Enregistrer le client"}
        </button>
      </div>
    </div>
  );
}
